import { Injectable, Injector, Signal, WritableSignal, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TableColumn, TableTypeNames } from '../column.service';
import { FilterSortPersistenceService, StoredSortState } from '../filter-sort-persistence.service';
import { TableColumnPreferenceService } from '../table-column-preference.service';
import { TableFilters } from '../../models/entities/table-filters';
import { PersistenceTier, TablePreferenceConcern, resolveTier } from './table-preference-tiers';
import type { TablePreferencesStore } from './table-preferences-store';

/**
 * SD-7878 — default (signal-first) implementation of {@link TablePreferencesStore}.
 *
 * Delegates to the persistence services that exist today, reflecting the current tier split:
 *   - columns            -> {@link TableColumnPreferenceService} (backend, SD-6481)
 *   - sort/filter/search -> {@link FilterSortPersistenceService} (localStorage, SD-7319)
 *
 * Each concern is exposed as a lazily-created signal seeded from the underlying store; writes go
 * through to that store AND update the signal, so templates/computed react without RxJS. Columns
 * bridge the backend service's Observable `watch` into a signal via `toSignal`.
 *
 * The backend migration of sort/filter/search (post-SD-7319) plugs in HERE: when
 * `resolveTier(table, concern)` returns `'backend'`, route those through the user-preferences API
 * instead of `FilterSortPersistenceService`. Consumers are untouched — that is the point of the seam.
 */
@Injectable({ providedIn: 'root' })
export class DefaultTablePreferencesStore implements TablePreferencesStore {
    private readonly _filterSort = inject(FilterSortPersistenceService);
    private readonly _columns = inject(TableColumnPreferenceService);
    private readonly _injector = inject(Injector);

    private readonly _sort = new Map<TableTypeNames, WritableSignal<StoredSortState | null>>();
    private readonly _search = new Map<TableTypeNames, WritableSignal<string | null>>();
    private readonly _filters = new Map<TableTypeNames, WritableSignal<TableFilters | null>>();
    private readonly _columnSignals = new Map<TableTypeNames, Signal<string[] | null>>();

    // --- sort (tier: local today) ---
    sort(table: TableTypeNames): Signal<StoredSortState | null> {
        return this.sortSignal(table).asReadonly();
    }

    setSort(table: TableTypeNames, sort: StoredSortState | null): void {
        this._filterSort.writeSort(table, sort);
        this.sortSignal(table).set(sort);
    }

    // --- search (tier: local today) ---
    search(table: TableTypeNames): Signal<string | null> {
        return this.searchSignal(table).asReadonly();
    }

    setSearch(table: TableTypeNames, value: string): void {
        this._filterSort.writeSearch(table, value);
        this.searchSignal(table).set(value === '' ? null : value);
    }

    // --- filters (tier: local today) ---
    filters(table: TableTypeNames): Signal<TableFilters | null> {
        return this.filtersSignal(table).asReadonly();
    }

    setFilters(table: TableTypeNames, filters: TableFilters): void {
        this._filterSort.writeFilters(table, filters);
        this.filtersSignal(table).set(filters);
    }

    // --- columns (tier: backend today) ---
    columns(table: TableTypeNames): Signal<string[] | null> {
        let columnSignal = this._columnSignals.get(table);
        if (!columnSignal) {
            columnSignal = toSignal(this._columns.watch(table), {
                initialValue: this._columns.read(table),
                injector: this._injector,
            });
            this._columnSignals.set(table, columnSignal);
        }

        return columnSignal;
    }

    storeColumns(table: TableTypeNames, columns: TableColumn[]): void {
        this._columns.store(table, columns);
    }

    persistColumns(table: TableTypeNames): void {
        this._columns.persist(table);
    }

    clearColumns(table: TableTypeNames): void {
        this._columns.clear(table);
    }

    tierFor(table: TableTypeNames, concern: TablePreferenceConcern): PersistenceTier {
        return resolveTier(table, concern);
    }

    private sortSignal(table: TableTypeNames): WritableSignal<StoredSortState | null> {
        let value = this._sort.get(table);
        if (!value) {
            value = signal(this._filterSort.readSort(table));
            this._sort.set(table, value);
        }

        return value;
    }

    private searchSignal(table: TableTypeNames): WritableSignal<string | null> {
        let value = this._search.get(table);
        if (!value) {
            value = signal(this._filterSort.readSearch(table));
            this._search.set(table, value);
        }

        return value;
    }

    private filtersSignal(table: TableTypeNames): WritableSignal<TableFilters | null> {
        let value = this._filters.get(table);
        if (!value) {
            value = signal(this._filterSort.readFilters(table));
            this._filters.set(table, value);
        }

        return value;
    }
}
